import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DangerousIcon from '@mui/icons-material/Dangerous'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { type Theme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { type SystemStyleObject } from '@mui/system'
import theme from 'assets/theme'
import KNLoader from 'components/KNLoader/KNLoader'
import { UserContext } from 'context/authentication/UserContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { getLogo } from 'global/helpers/getLogo'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import { validateEmail } from 'global/helpers/validators'
import i18n from 'i18n'
import { type ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { resetPasswordTranslations } from './ResetPassword.data'

const ResetPassword = (): ReactElement => {
  // Screen specific //
  const urlParams = new URLSearchParams(window.location.search)
  const oobCode = urlParams.get('oobCode')
  const [codeError, setCodeError] = useState('')
  const [loading, setLoading] = useState(false)
  const environment = import.meta.env.VITE_API_ENV
  const backButtonLink = `https://${environment !== 'prod' ? environment + '.' : ''}carriersolution.kuehne-nagel.com/login`

  // Password reset //
  const [password, setPassword] = useState('')
  const [weakPassword, setWeakPassword] = useState(false)
  const [passwordResetError, setPasswordResetError] = useState('')
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const handleTogglePasswordVisibility = (): void => {
    setShowPassword(!showPassword)
  }

  // Email reset resend //
  const [email, setEmail] = useState('')
  const [emailSendingError, setEmailSendingError] = useState('')
  const [emailSendingSuccess, setEmailSendingSuccess] = useState(false)

  // Context
  const { resetUser, verifyPasswordResetCode, confirmPasswordReset } = useContext(UserContext)

  const sendResetEmail = async (): Promise<any> => {
    await resetUser(email).then(
      () => {
        setEmailSendingSuccess(true)
        setLoading(false)
        setEmail('')
      },
      (error: unknown) => {
        setEmailSendingError(getRequestErrorMessage(error) ?? '')
        setLoading(false)
        setEmail('')
      },
    )
  }

  const setSendResetEmail = useCallback((): void => {
    void sendResetEmail()
  }, [email])

  const resetPassword = async (password: string): Promise<any> => {
    setLoading(true)
    if (oobCode) {
      return await confirmPasswordReset(oobCode, password).then(
        () => {
          setPasswordResetSuccess(true)
          setLoading(false)
          setPassword('')
          analyticsEvent('polestar_password_reset')
        },
        () => {
          setPasswordResetError('invalidactioncode')
          setLoading(false)
          setPassword('')
        },
      )
    }
  }

  const setResetPassword = useCallback((): void => {
    if (weakPassword || password.length === 0) {
      return
    }
    void resetPassword(password)
  }, [password])

  const validateActionCode = (actionCode: string): void => {
    setLoading(true)
    verifyPasswordResetCode(actionCode)
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setCodeError('invalidactioncode')
        setLoading(false)
      })
  }

  useEffect(() => {
    analyticsPageView('polestar/reset_pw')
    // Check the oobCode for the status of the password reset code
    validateActionCode(oobCode ?? '')
  }, [])

  // Data
  const { translation } = resetPasswordTranslations()

  return (
    <Container maxWidth="xs">
      <Box my={10} sx={{ position: 'relative' }}>
        {/* Error or code invalid/ expired */}
        {codeError && !loading && (
          <Alert variant="filled" severity="info" sx={{ my: 2 }}>
            {i18n.t(`shared.reset_password.${codeError}`)}
          </Alert>
        )}
        {/* Loading */}
        {loading && (
          <KNLoader>
            <Typography>{translation.loading}</Typography>
          </KNLoader>
        )}
        {/* Email sent again */}
        {(emailSendingError || emailSendingSuccess) && !loading && (
          <>
            {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
            <Box my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Stack
                spacing={2}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                {emailSendingSuccess ? (
                  <CheckCircleIcon color="success" sx={{ height: '100px', width: '100px' }} />
                ) : (
                  <DangerousIcon color="error" sx={{ height: '100px', width: '100px' }} />
                )}
                <Box my={3}>
                  <Typography variant="h4" color="black">
                    {emailSendingSuccess ? translation.resetSuccessful : translation.resetFail}
                  </Typography>
                  <Typography variant="p3" color="grey">
                    {emailSendingSuccess ? translation.emailResetSuccessfulMsg : translation.resetFailMsg}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </>
        )}
        {/* Reset done */}
        {(passwordResetSuccess || passwordResetError) && !loading && (
          <>
            {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
            <Box my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Stack
                spacing={2}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                {passwordResetSuccess ? (
                  <CheckCircleIcon color="success" sx={{ height: '100px', width: '100px' }} />
                ) : (
                  <DangerousIcon color="error" sx={{ height: '100px', width: '100px' }} />
                )}
                <Box my={3}>
                  <Typography variant="h4" color="black">
                    {passwordResetSuccess ? translation.resetSuccessful : translation.resetFail}
                  </Typography>
                  <Typography variant="p3" color="grey">
                    {passwordResetSuccess ? translation.resetSuccessfulMsg : translation.resetFailMsg}
                  </Typography>
                </Box>
                <a href={backButtonLink} style={{ textDecoration: 'none' }}>
                  <Button variant="text" color="primary" startIcon={<ArrowBackIcon />}>
                    {translation.backButton}
                  </Button>
                </a>
              </Stack>
            </Box>
          </>
        )}
        {/* Reset active state */}
        {!passwordResetError && !passwordResetSuccess && codeError === '' && !loading && (
          <Box my={3}>
            <form
              onSubmit={(e): void => {
                e.preventDefault()
                setResetPassword()
              }}
            >
              {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
              <Stack spacing={2} px={{ xs: 0, sm: 2 }}>
                <Box>
                  <Typography
                    variant="p3"
                    mb={0.5}
                    sx={{
                      color: ({ palette: { grey } }: Theme): string =>
                        passwordResetError > '' ? grey[300] : grey[600],
                      fontWeight: ({ typography: { fontWeightLight } }: Theme): number => fontWeightLight,
                    }}
                  >
                    {translation.new_password}
                  </Typography>
                  <TextField
                    error={weakPassword}
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    onChange={(e): void => {
                      const newPassword = e.target.value
                      setWeakPassword(newPassword.length > 0 && newPassword.length < 6)
                      setPassword(newPassword)
                    }}
                    helperText={translation.resetPasswordRequirements}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            aria-label="toggle password visibility"
                            sx={({ palette: { grey } }): SystemStyleObject<Theme> => ({
                              color: grey[300],
                              transition: 'color 200ms',
                              '&:hover': {
                                color: grey[400],
                              },
                            })}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        ),
                      },
                    }}
                  />
                </Box>
                <Box my={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={(): void => setResetPassword()}
                    disabled={weakPassword}
                    sx={{
                      fontSize: ({ typography: { size } }: Theme): string => size.md,
                      height: '44px',
                    }}
                  >
                    {translation.resetPasswordButton}
                  </Button>
                </Box>
                <Box my={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <a href={backButtonLink} style={{ textDecoration: 'none' }}>
                    <Button variant="text" color="primary" startIcon={<ArrowBackIcon />}>
                      {translation.backButton}
                    </Button>
                  </a>
                </Box>
              </Stack>
            </form>
          </Box>
        )}
        {/* Email resend state */}
        {codeError && !emailSendingError && !emailSendingSuccess && !loading && (
          <Box my={3}>
            <form>
              {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
              <Stack spacing={2} px={{ xs: 0, sm: 2 }}>
                <Box>
                  <Typography
                    variant="p5"
                    mb={0.5}
                    sx={{
                      color: ({ palette: { grey } }: Theme): string => grey[600],
                      fontWeight: ({ typography: { fontWeightLight } }: Theme): number => fontWeightLight,
                    }}
                  >
                    {translation.email}
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    type="email"
                    autoComplete="email"
                    onChange={(e): void => setEmail(e.target.value)}
                  />
                </Box>
                <Box my={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!validateEmail(email)}
                    onClick={(): void => setSendResetEmail()}
                    sx={{
                      fontSize: ({ typography: { size } }: Theme): string => size.md,
                      height: '44px',
                    }}
                  >
                    {translation.resetPasswordButton}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Box>
        )}
      </Box>
    </Container>
  )
}

export default ResetPassword
